'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ChevronDoubleRightIcon } from '@heroicons/react/24/solid'
import { PhotoIcon } from '@heroicons/react/24/outline'

const ImageOrPlaceholder = ({
  src,
  alt,
  href,
}: {
  src: string
  alt: string
  href: string | null
}) => {
  const [hasError, setHasError] = useState(false)
  const showPlaceholder = !src || hasError

  const placeholder = (
    <div className="w-full aspect-video flex items-center justify-center bg-gray-200 rounded">
      <PhotoIcon className="w-12 h-12 text-gray-400" />
    </div>
  )

  if (showPlaceholder) {
    return href ? <Link href={href} className="block">{placeholder}</Link> : placeholder
  }

  const img = (
    <img
      className="w-full aspect-video object-cover rounded"
      src={src}
      alt={alt}
      onError={() => setHasError(true)}
    />
  )

  return href ? <Link href={href} className="block">{img}</Link> : img
}

const ArticleRow = ({
  title,
  url,
  blurb,
  thumbnailSrc,
}: ArticleRowProps) => {
  const href = !url ? null : url.startsWith('/') ? url : `/${url}`

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-6 mb-8 pb-8 border-b border-gray-200 last:border-b-0 md:py-4 md:px-4 md:-mx-4 rounded-lg transition-colors hover:bg-[#f2f2f2]">
      <div className="flex-shrink-0 md:w-1/4">
        <ImageOrPlaceholder
          src={thumbnailSrc || ''}
          alt={title || ''}
          href={href || null}
        />
      </div>
      <div className="flex-1 min-w-0">
        {href ? (
          <Link href={href} className="no-underline group">
            <h2 className="text-2xl font-bold text-black mb-2 group-hover:text-vi-red transition-colors">
              {title}
            </h2>
          </Link>
        ) : (
          <h2 className="text-2xl font-bold text-black mb-2">{title}</h2>
        )}
        <p className="text-gray-700 mb-4">{blurb}</p>
        {href && (
          <Link
            href={href}
            className="inline-flex items-center gap-1 text-black text-xl hover:text-vi-red transition-colors"
          >
            Read more
            <ChevronDoubleRightIcon className="w-5 h-5" />
          </Link>
        )}
      </div>
    </div>
  )
}

export default ArticleRow

interface ArticleRowProps {
  title: string
  url?: string | null
  blurb?: string | null
  thumbnailSrc: string
}
